/**
 * Escopo por construção (T59, ESC-01, `ADR-0004` D1/D2/D4).
 *
 * O escopo deixa de ser convenção e passa a ser invariante: **nenhuma regra
 * individual escreve condição de obra**, porque nenhuma regra individual a
 * escreve — `aplicarEscopo` a injeta em todas, depois de o compilador já ter
 * traduzido a concessão. É o que fecha a invariante **E5**: uma regra nova não
 * pode escapar do escopo por esquecimento, porque ela não participa da decisão.
 *
 * ## `VISIBILIDADE` não existe, e o motivo importa
 *
 * A versão anterior do desenho tinha `VISIBILIDADE: Record<Recurso,
 * Visibilidade>` com o valor `global`, e `aplicarEscopo` fazia curto-circuito
 * nele — devolvia a regra **sem escopo, independentemente do escopo do próprio
 * usuário**. Um cliente lendo `Obra` saía sem filtro. O `ADR-0004` D1 apagou o
 * valor: a dispensa do filtro depende do escopo do USUÁRIO (`"todas"`), nunca do
 * recurso.
 *
 * ## Escopo existe só para `tipo: "cliente"`
 *
 * `PADRONIZACAO-ROLES.md §8.1`, e é a decisão que fez `rdo_by_obra` ser marcada
 * para morrer: Gali lê todas as obras do recurso que seu papel alcança
 * (decisões 1 e 3 do time em `ADR-0004`), e "da sua obra" para um Gali é
 * **relação** na entidade, não escopo — é o que `RELACOES` expressa. A `D3` do
 * AD-031 e a `D6` do AD-034 confirmaram isso duas vezes, para OS e para RDO.
 *
 * ## Duas listas, uma por dimensão — e nunca misturadas
 *
 * Decisão do usuário de 2026-09-03, em `docs/contratos-backend.md` §10.2: `TAF`
 * escopa por `proposta`, que não é obra nem fabricação. `CAMPO_ESCOPO` deixou de
 * ser `recurso → campo` e virou `recurso → { campo, dimensao }`, e o escopo do
 * usuário passou a ter duas listas. Misturar as duas é a falha invisível que a
 * `dimensao` existe para impedir: um id de obra num filtro de proposta ou casa
 * por acidente ou nega em silêncio, e nenhuma das duas aparece em log.
 *
 * A invariante **E1** do `ADR-0004` muda de forma junto: não há mais uma lista
 * só com namespace único, e sim **uma lista por dimensão, com E1 valendo dentro
 * de cada uma** (`B10.3` e `B10.5` do contrato de backend).
 *
 * ## O que este arquivo NÃO faz, de propósito
 *
 * Não liga nada. `defineAbilityFor` continua montando dos dezoito arquivos
 * imperativos, e `cliente.ts:29` continua escrevendo a condição de obra à mão —
 * apagá-la é a **T61**, e a ligação do agregador é a **T129**. Também não deriva
 * o escopo de um `User`: hoje o `UserModel` guarda `obra: string[]` e não tem
 * `propostas`, e a fonte das duas listas é `GET /me/permissoes`
 * (`docs/contratos-backend.md` §6, invariante `B10.6`). Inventar aqui uma
 * conversão de `user.obra` para as duas listas decidiria, por conta própria, o
 * que um cliente enxerga em `TAF`.
 */
import type { Acao, Recurso } from "./catalogo/vocabulario";
import type { RegraCompilada } from "./compilador";
// `CAMPO_ESCOPO`/`Dimensao` moraram aqui até a T143; moveram para
// `catalogo/campo-escopo.ts` para quebrar o ciclo de import com
// `compilador.ts` (T132 passou a lê-lo lá). Importados (para uso abaixo, em
// `aplicarEscopo`) E reexportados (para nenhum importador existente precisar
// mudar — `export { X } from "..."` sozinho não traria `X` para o escopo
// deste arquivo, e este arquivo também os usa).
import { CAMPO_ESCOPO, type Dimensao } from "./catalogo/campo-escopo";
export { CAMPO_ESCOPO, type Dimensao };

/**
 * Id de obra, de fabricação ou de proposta.
 *
 * `ADR-0004` D2: o campo do registro é polimórfico e o que se testa é o id.
 * `$in` é match exato de string, então a chave é **id, nunca nome** — invariante
 * `B10.1` do contrato de backend. Nome colide e aceita typo, e há evidência de
 * que os valores de hoje não são chaves normalizadas: `src/assets/obrasPosVendas.js`
 * guarda `"PARACATU 1 "`, com espaço à direita preservado.
 */
export type ChaveEscopo = string;

/** O escopo que dispensa o filtro. Não é um recurso "global": é o usuário que vê tudo. */
export const TODAS = "todas";

/** Lista de chaves de uma dimensão, ou `"todas"`. */
export type ListaDeEscopo = readonly ChaveEscopo[] | typeof TODAS;

/**
 * O escopo do usuário: uma lista por dimensão.
 *
 * `GET /me/permissoes` devolve as duas como listas distintas (`B10.6`). Uma
 * lista só reintroduziria exatamente a mistura que a `dimensao` existe para
 * evitar.
 */
export interface EscopoDoUsuario {
  readonly empreendimentos: ListaDeEscopo;
  readonly propostas: ListaDeEscopo;
}

/**
 * Recursos em que o usuário só enxerga os registros em que tem relação — nem
 * mesmo um Gali vê tudo.
 *
 * Duas entradas. `TicketInteracao` é o que sobrou da tabela `VISIBILIDADE`
 * inteira: decisão 7 do time, "usuário lê **só o seu**". `ProcessoJuridico`
 * entrou na conferência de acessos (JUR-01, 2026-10-01), que revogou a decisão 6
 * ("`juridico.leitura` vê todos os processos"): a leitura do Jurídico lê só o
 * processo em que a pessoa é responsável de DP ou de segurança.
 *
 * A lista não é consumida por `aplicarEscopo`: a restrição é declarada no
 * catálogo, como `exigeRelacao` na concessão de leitura, e o compilador já sabe
 * traduzi-la. O valor de existir aqui é ser o alvo do teste de invariante — uma
 * concessão de leitura sobre um recurso desta lista **sem** `exigeRelacao` é
 * defeito, e reprova.
 */
export const SO_POR_RELACAO: readonly Recurso[] = ["TicketInteracao", "ProcessoJuridico"];

/**
 * As ações que contam como leitura para a partição.
 *
 * É o conjunto que o desenho fixa (`ANALISE-CONTROLE-ACESSO.md` §4.5:
 * `ACOES_LEITURA = new Set(["read"])`), e nenhuma fonte estende a lista.
 *
 * **A classificação é hoje inerte, e vale registrar por quê.** A partição
 * existia para escolher entre `escopo.leitura` e `escopo.atuacao`, que era o
 * desenho anterior. A decisão de 2026-09-03 trocou as duas listas por uma lista
 * **por dimensão**, e nenhuma fonte diz que alguma dimensão volte a se dividir
 * por classe de ação — a análise registra isso como pendência aberta. Enquanto
 * as duas classes recebem a mesma lista, separar as regras não muda decisão
 * nenhuma: é a costura onde a distinção entra, se entrar. Ampliar este conjunto
 * antes disso seria decidir sem fonte.
 */
export const ACOES_DE_LEITURA: readonly Acao[] = ["read"];

function ehAcaoDeLeitura(acao: Acao): boolean {
  return ACOES_DE_LEITURA.includes(acao);
}

/**
 * Uma regra de ação mista vira duas: a de leitura e a de escrita.
 *
 * `AC2` do `spec.md` pede que o compilador emita regras separadas para as duas
 * classes **antes** de injetar escopo, e só para `tipo: "cliente"`. Por isso a
 * partição mora aqui e não em `compilarConcessao`: o compilador não conhece
 * `tipo`, e separar para todo mundo dobraria a contagem de regras de todo o
 * catálogo sem que ninguém tivesse pedido.
 *
 * Duas regras com o mesmo par recurso/condição decidem igual a uma regra com as
 * duas ações — o CASL avalia o conjunto em OR. A partição não muda resposta;
 * ela cria o lugar onde a resposta pode divergir.
 */
export function particionarPorClasseDeAcao(regras: readonly RegraCompilada[]): RegraCompilada[] {
  return regras.flatMap((regra) => {
    const leitura = regra.action.filter(ehAcaoDeLeitura);
    const escrita = regra.action.filter((acao) => !ehAcaoDeLeitura(acao));
    if (leitura.length === 0 || escrita.length === 0) return [regra];

    return [
      { ...regra, action: leitura },
      { ...regra, action: escrita },
    ];
  });
}

/**
 * Lista de escopo crua vira lista utilizável — invariantes **E2** e **E3**.
 *
 * **E3, fail-closed:** ausente, `null`, string solta, objeto — tudo resolve para
 * `[]`, que nega. Nunca `undefined`, e nunca `"todas"`: o caminho do erro não
 * pode ser o caminho que libera. É o substituto tipado do `testaJSON` de
 * `UserModel.ts:114`, que hoje devolve o que vier.
 *
 * **E2, normalização:** `trim` em cada chave, porque `$in` é match exato — um
 * espaço à direita nega acesso sem dizer nada. Entrada que não é string, ou que
 * fica vazia depois do `trim`, sai da lista: uma chave `""` casaria com um
 * registro de campo vazio, que é acesso por acidente.
 */
export function normalizarLista(bruta: unknown): ListaDeEscopo {
  if (bruta === TODAS) return TODAS;
  if (!Array.isArray(bruta)) return [];

  return bruta
    .filter((chave): chave is string => typeof chave === "string")
    .map((chave) => chave.trim())
    .filter((chave) => chave.length > 0);
}

/** O escopo cru do usuário, já normalizado nas duas dimensões. */
export function normalizarEscopo(bruto: Partial<EscopoDoUsuario> | undefined): EscopoDoUsuario {
  return {
    empreendimentos: normalizarLista(bruto?.empreendimentos),
    propostas: normalizarLista(bruto?.propostas),
  };
}

/** O que `aplicarEscopo` precisa saber do usuário. Nada além disso. */
export interface UsuarioComEscopo {
  readonly tipo: string;
  readonly escopo?: Partial<EscopoDoUsuario>;
}

/**
 * Injeta o escopo em todas as regras, ou devolve as regras intactas.
 *
 * Para quem não é `cliente` não há escopo: as regras saem como entraram
 * (`PADRONIZACAO-ROLES.md §8.1`), e é isso que faz uma concessão de ação mista
 * de um Gali continuar lendo tudo o que seu papel alcança.
 *
 * Para `cliente`, na ordem:
 *
 * 1. as regras de ação mista são partidas em leitura e escrita;
 * 2. recurso **sem** entrada em `CAMPO_ESCOPO` some — é a invariante **E4**, e
 *    ela nega em vez de liberar. Vale também para `subject: "all"`, que não tem
 *    dimensão nenhuma;
 * 3. a lista é escolhida **pela `dimensao` do recurso**, nunca pela outra;
 * 4. `"todas"` dispensa o filtro; qualquer outra coisa vira
 *    `{ [campo]: { $in: lista } }`, composto com a condição que a regra já
 *    trazia — relação e estado continuam valendo, agora em AND com o escopo.
 */
export function aplicarEscopo(
  regras: readonly RegraCompilada[],
  usuario: UsuarioComEscopo,
): RegraCompilada[] {
  if (usuario.tipo !== "cliente") return [...regras];

  const escopo = normalizarEscopo(usuario.escopo);

  return particionarPorClasseDeAcao(regras).flatMap((regra) => {
    const alvo = CAMPO_ESCOPO[regra.subject];
    if (!alvo) return [];

    const lista = alvo.dimensao === "proposta" ? escopo.propostas : escopo.empreendimentos;
    if (lista === TODAS) return [regra];

    return [
      {
        ...regra,
        conditions: { ...(regra.conditions ?? {}), [alvo.campo]: { $in: [...lista] } },
      },
    ];
  });
}
